import {
  ComboboxPopover,
  type Option,
} from '@/components/ui/combobox/combobox-popover';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import {
  PDKDeliveryQueueIcon,
  PDKRemoveFromDeliveryQueueIcon,
} from '@/components/ui/pdk-icons';
import HotKeyListener from '@/components/ux/hot-key-listener';
import { HOTKEYS } from '@/lib/hotkeys';
import { cn } from '@/lib/utils';

import { useDealContext } from '@/contexts/deal-provider';
import { useDealByShortId } from '@/hooks/use-deals-selectors';
import type { Deal } from '@/models/registry';
import { useMemo, useRef } from 'react';
import DeliveryQueueBadge from './delivery-queue-badge';
import getDeliveryQueueHealthClasses from './get-delivery-queue-health-classes';
import useDeliveryQueueMutations from './hooks/use-delivery-queue-mutations';

export const getDeliveryQueueOptionsMap = (
  className: Record<string, boolean>,
): (Option<'yes' | 'no'> & {
  booleanValue: boolean;
})[] => [
  {
    value: 'yes',
    label: 'Yes',
    icon: (
      <PDKDeliveryQueueIcon
        className={cn('size-3.5 cursor-pointer', className)}
      />
    ),
    booleanValue: true,
    abbreviation: 'DQ-ed',
  },
  {
    value: 'no',
    label: 'No',
    icon: <PDKRemoveFromDeliveryQueueIcon className="size-3.5" />,
    booleanValue: false,
    abbreviation: 'Not DQ-ed',
  },
];

export type DeliveryQueueSelectorProps = {
  withHotkeyListener?: boolean;
  pickupDate: Deal['pickup_date'];
  delivery_date: Deal['delivery_date'];
  date_added: Deal['date_added_to_customer_delivery_queue'];
  dealId: Deal['id'];
  useAbbreviation?: boolean;
  className?: string;
};

export function DeliveryQueueSelector({
  withHotkeyListener = false,
  pickupDate,
  delivery_date,
  date_added,
  dealId,
  useAbbreviation = false,
  className,
}: DeliveryQueueSelectorProps) {
  const { enqueueDealMutation, dequeueDealMutation } =
    useDeliveryQueueMutations({ dealId });

  const { currentShortId } = useDealContext();
  const deal = useDealByShortId(currentShortId);

  const handleDeliveryQueueChange = () => {
    // if there is a date_added already, remove it, otherwise create a new date string and pass it to the mutation
    if (date_added) {
      dequeueDealMutation.mutate();
    } else {
      enqueueDealMutation.mutate({
        date_added: new Date().toISOString(),
      });
    }
  };

  const buttonRef = useRef<HTMLDivElement>(null);

  const deliveryQueueHealthClasses = useMemo(
    () =>
      getDeliveryQueueHealthClasses({
        pickupDateOrDeliveryDate: pickupDate || delivery_date,
        deal: deal!,
      }),
    [pickupDate, delivery_date, deal],
  );

  const DELIVERY_QUEUE_OPTIONS_MAP = useMemo(
    () => getDeliveryQueueOptionsMap(deliveryQueueHealthClasses),
    [deliveryQueueHealthClasses],
  );

  const currentOption = useMemo(() => {
    return DELIVERY_QUEUE_OPTIONS_MAP.find(
      (option) => option.value === (date_added ? 'yes' : 'no'),
    );
  }, [date_added]);

  return (
    <ComboboxPopover
      // DEV NOTE: this is a hack to force a re-render when the delivery queue health classes change
      key={JSON.stringify(deliveryQueueHealthClasses)}
      endAdornment={
        <KeyboardKeyIcon>{HOTKEYS.DEAL_DELIVERY_QUEUE}</KeyboardKeyIcon>
      }
      placeholder="Add to delivery queue..."
      currentOption={currentOption}
      onChange={handleDeliveryQueueChange}
      emptyMessage="No Delivery Queue options"
      options={DELIVERY_QUEUE_OPTIONS_MAP}
      side="right"
      align="start"
    >
      <div ref={buttonRef}>
        <DeliveryQueueBadge
          pickupDate={pickupDate}
          delivery_date={delivery_date}
          // vehicle={vehicle}
          date_added={date_added}
          useAbbreviation={useAbbreviation}
          className={className}
        />
        {withHotkeyListener && (
          <HotKeyListener
            hotkeys={HOTKEYS.DEAL_DELIVERY_QUEUE}
            onHotkeyPressed={() => buttonRef.current?.click()}
          />
        )}
      </div>
    </ComboboxPopover>
  );
}
